import React, { useEffect, useRef, useState } from 'react';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { Link } from 'react-router-dom';
import { BREAKPOINTS } from '@epcbuilder/lib/hooks/media-queries/useMediaQueries';

const measures = [
  {
    id: 'air-source-heat-pumps',
    name: 'Air Source Heat Pump',
    image: 'air-source-heat-pump.png',
    background: 'rgba(244, 162, 97, 0.16)',
  },
  {
    id: 'solar-pv',
    name: 'Solar Photovoltaic Panels',
    image: 'solar-photovoltaic-panels.png',
    background: 'rgba(233, 196, 105, 0.16)',
  },
  {
    id: 'heating-controls',
    name: 'Heating Controls',
    image: 'heating-controls.png',
    background: 'rgba(42, 156, 142, 0.08)',
  },
  {
    id: 'boiler',
    name: 'Boiler',
    image: 'boiler.png',
    background: 'rgba(38, 70, 83, 0.08)',
  },
  {
    id: 'cavity-wall-insulation',
    name: 'Cavity Wall Insulation',
    image: 'cavity-wall-insulation.png',
    background: 'rgba(244, 162, 97, 0.16)',
  },
  {
    id: 'internal-wall-insulation',
    name: 'Internal Wall Insulation',
    image: 'internal-wall-insulation.png',
    background: 'rgba(233, 196, 105, 0.16)',
  },
  {
    id: 'loft-insulation',
    name: 'Loft Insulation',
    image: 'loft-insulation.png',
    background: 'rgba(42, 156, 142, 0.08)',
  },
  {
    id: 'roof-insulation',
    name: 'Room In Roof Insulation',
    image: 'room-in-roof-insulation.png',
    background: 'rgba(38, 70, 83, 0.08)',
  },
  {
    id: 'underfloor-insulation',
    name: 'Underfloor Insulation',
    image: 'underfloor-insulation.png',
    background: 'rgba(244, 162, 97, 0.16)',
  },
];

const InstallationMeasures = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(3.5);
  const [pageCount, setPageCount] = useState(0);

  useEffect(() => {
    const updateItemsPerPage = () => {
      const width = window.innerWidth;
      if (width >= BREAKPOINTS.LARGE_DESKTOP) setItemsPerPage(3.5);
      else if (width >= BREAKPOINTS.DESKTOP) setItemsPerPage(2.5);
      else if (width >= BREAKPOINTS.TABLET) setItemsPerPage(1.5);
      else setItemsPerPage(1);
    };

    updateItemsPerPage();
    window.addEventListener('resize', updateItemsPerPage);
    return () => window.removeEventListener('resize', updateItemsPerPage);
  }, []);

  useEffect(() => {
    const pages = Math.ceil(measures.length / itemsPerPage);
    setPageCount(pages);
    scrollToPage(1);
  }, [itemsPerPage]);

  const scrollToPage = (page: number) => {
    const container = containerRef.current;
    if (!container) return;

    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    const scrollAmount = (maxScrollLeft / (pageCount - 1)) * page;

    container.scrollTo({ left: scrollAmount, behavior: 'smooth' });
    setActiveIndex(page);
  };

  const handleScroll = () => {
    const container = containerRef.current;
    if (container) {
      const maxScrollLeft = container.scrollWidth - container.clientWidth;
      const scrollLeft = container.scrollLeft;
      const page = Math.round((scrollLeft / maxScrollLeft) * (pageCount - 1));
      setActiveIndex(page);
    }
  };

  const getHoverColor = (bg: string) => {
    const colorMap: { [key: string]: string } = {
      'rgba(244, 162, 97, 0.16)': 'rgba(244, 162, 97, 0.24)',
      'rgba(233, 196, 105, 0.16)': 'rgba(233, 196, 105, 0.24)',
      'rgba(42, 156, 142, 0.08)': 'rgba(42, 156, 142, 0.16)',
      'rgba(38, 70, 83, 0.08)': 'rgba(38, 70, 83, 0.16)',
    };
    return colorMap[bg] || bg;
  };

  return (
    <div className="w-full py-8 lg:py-16">
      <div className="mx-auto max-w-[83rem] px-6">
        <h2 className="mb-4 text-left text-2xl font-bold lg:mb-12 lg:text-4xl xl:text-5xl">
          The installation measures we offer
        </h2>
      </div>
      <div className="relative">
        <div
          className="hide-scrollbar flex snap-x snap-mandatory gap-6 overflow-x-auto scroll-smooth px-6"
          ref={containerRef}
          onScroll={handleScroll}
        >
          {measures.map((measure, index) => (
            <Link to={`/install-measures#${measure.id}`} key={index}>
              <div
                key={index}
                className="group flex w-[360px] shrink-0 snap-center flex-col items-center rounded-[10px] p-2 pb-4 transition-colors duration-200"
                style={{
                  backgroundColor: measure.background,
                }}
                onMouseEnter={(e) => {
                  const target = e.currentTarget;
                  target.style.backgroundColor = getHoverColor(measure.background);
                }}
                onMouseLeave={(e) => {
                  const target = e.currentTarget;
                  target.style.backgroundColor = measure.background;
                }}
              >
                <div className="flex size-[344px] items-center justify-center rounded-[10px] bg-[#FAFAFA]">
                  <img
                    src={`/images/installation-measures/${measure.image}`}
                    alt={measure.name}
                    className="w-[257px] object-contain"
                  />
                </div>
                <p className="mt-4 text-center text-base font-bold text-[#153B37]">{measure.name}</p>
              </div>
            </Link>
          ))}
        </div>
        <div className="mt-8 flex flex-row items-center justify-between gap-4 px-4 lg:px-[204px]">
          <button onClick={() => scrollToPage(Math.max(activeIndex - 1, 0))}>
            <MdChevronLeft size={50} />
          </button>
          <div className="flex gap-2">
            {Array.from({ length: pageCount }).map((_, i) => (
              <div
                key={i}
                className="size-2 rounded-full"
                style={{ backgroundColor: i === activeIndex ? '#2A9C8E' : '#A2D6CF' }}
              />
            ))}
          </div>
          <button onClick={() => scrollToPage(Math.min(activeIndex + 1, pageCount - 1))}>
            <MdChevronRight size={50} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallationMeasures;
